import { useEffect, useId, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

/**
 * A drop-down on the dark top bar - Craig's ruling of 29 September 2026.
 *
 * Three of them: **Setup** (the operator's settings pages), the **agency
 * name** (Settings, Feedback, Sign out) and **Operator** (who is signed in,
 * the payments sentence, Sign out). They are what let each bar fit on one row.
 *
 * A menu button in the WAI-ARIA sense:
 *
 * - a real `<button>` with `aria-haspopup="menu"` and `aria-expanded`;
 * - opening it moves focus to the first item, and **the arrow keys** move
 *   between items (wrapping), with Home and End;
 * - it closes on **choosing an item**, on **Escape** (focus goes back to the
 *   button), on **Tab** (focus carries on as it would) and on **a press
 *   outside it**;
 * - **only one is open at a time**: the bar owns which one, through
 *   `openId` and `onOpenChange`, so opening one closes the other.
 *
 * Items are links or actions. A header (the full agency name, the operator's
 * address and payments sentence) sits above the items and is not one: it is
 * there to be read, not chosen.
 */
export type MenuEntry =
  | { kind: 'link'; to: string; label: string; current?: boolean }
  | { kind: 'action'; label: string; onSelect: () => void }
  | { kind: 'divider' };

export function DropdownMenu({
  id,
  label,
  accessibleLabel,
  active = false,
  align = 'left',
  header,
  entries,
  openId,
  onOpenChange,
}: {
  /** Which menu this is, for `openId`. */
  id: string;
  /** What the button shows. The down arrow is added here. */
  label: ReactNode;
  /** The button's name when `label` is shortened (a long agency name). */
  accessibleLabel?: string;
  /** The page being shown is one of this menu's: the button is blue. */
  active?: boolean;
  align?: 'left' | 'right';
  header?: ReactNode;
  entries: readonly MenuEntry[];
  openId: string | null;
  onOpenChange: (next: string | null) => void;
}): React.JSX.Element {
  const open = openId === id;
  const buttonId = useId();
  const menuId = useId();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);

  function items(): HTMLElement[] {
    return Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);
  }

  function close(returnFocus: boolean): void {
    onOpenChange(null);
    if (returnFocus) buttonRef.current?.focus();
  }

  useEffect(() => {
    if (!open) return;
    items()[0]?.focus();

    function onPointerDown(event: Event): void {
      const target = event.target as Node | null;
      if (target && wrapperRef.current?.contains(target)) return;
      onOpenChange(null);
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
    // `onOpenChange` is the bar's state setter; it does not change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function onMenuKeyDown(event: KeyboardEvent<HTMLUListElement>): void {
    const all = items();
    const at = all.indexOf(document.activeElement as HTMLElement);
    const move = (next: number): void => {
      event.preventDefault();
      all[(next + all.length) % all.length]?.focus();
    };
    switch (event.key) {
      case 'ArrowDown':
        move(at + 1);
        break;
      case 'ArrowUp':
        move(at - 1);
        break;
      case 'Home':
        move(0);
        break;
      case 'End':
        move(all.length - 1);
        break;
      case 'Escape':
        event.preventDefault();
        close(true);
        break;
      case 'Tab':
        close(false);
        break;
      default:
        break;
    }
  }

  const itemClass =
    'block w-full px-4 py-2 text-left text-sm whitespace-nowrap focus:bg-raise focus:outline-none hover:bg-raise';

  return (
    <div ref={wrapperRef} className="relative shrink-0">
      <button
        ref={buttonRef}
        id={buttonId}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={accessibleLabel}
        onClick={() => {
          onOpenChange(open ? null : id);
        }}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' && !open) {
            event.preventDefault();
            onOpenChange(id);
          }
        }}
        className={`inline-flex max-w-full items-center gap-1.5 rounded-control px-3 py-1.5 text-sm font-medium transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue ${
          active ? 'text-blue' : 'text-ink-2 hover:text-ink'
        }`}
      >
        {label}
        <svg
          aria-hidden="true"
          viewBox="0 0 12 12"
          className={`h-3 w-3 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
        >
          <path d="M2.5 4.5 6 8l3.5-3.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      </button>

      {open ? (
        <div
          data-testid={`menu-${id}`}
          className={`absolute top-full z-40 mt-2 min-w-48 rounded-box border border-line bg-ground-2 py-1 shadow-lg ${
            align === 'right' ? 'right-0' : 'left-0'
          }`}
        >
          {header ? (
            <div className="border-b border-line px-4 pt-2 pb-3 text-sm">{header}</div>
          ) : null}
          <ul
            ref={menuRef}
            id={menuId}
            role="menu"
            aria-labelledby={buttonId}
            onKeyDown={onMenuKeyDown}
          >
            {entries.map((entry, index) =>
              entry.kind === 'divider' ? (
                <li key={`divider-${String(index)}`} role="separator" className="my-1 border-t border-line" />
              ) : entry.kind === 'link' ? (
                <li key={entry.to} role="none">
                  <Link
                    role="menuitem"
                    tabIndex={-1}
                    to={entry.to}
                    aria-current={entry.current ? 'page' : undefined}
                    onClick={() => {
                      close(false);
                    }}
                    className={`${itemClass} ${entry.current ? 'font-semibold text-blue' : 'text-ink-2 hover:text-ink'}`}
                  >
                    {entry.label}
                  </Link>
                </li>
              ) : (
                <li key={entry.label} role="none">
                  <button
                    type="button"
                    role="menuitem"
                    tabIndex={-1}
                    onClick={() => {
                      close(false);
                      entry.onSelect();
                    }}
                    className={`${itemClass} text-ink-2 hover:text-ink`}
                  >
                    {entry.label}
                  </button>
                </li>
              ),
            )}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
